import React, { useEffect, useState } from "react";
import axios from "axios";
import { Table, Button, Modal, Form, Input, message, Spin, Upload } from "antd";
import { BASE_URL } from "../constants/Constants";
import {
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const Subcategories = () => {
  const userId = localStorage.getItem("userId");
  const [categories, setCategories] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [form] = Form.useForm();
  
  // State variables
  const [newSubcategoryName, setNewSubcategoryName] = useState("");
  const [displayOrder, setDisplayOrder] = useState("");
  const [updatedDisplayOrder, setUpdatedDisplayOrder] = useState("");
  const [updateSubcategoryName, setUpdateSubcategoryName] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [newSubcategoryDescription, setNewSubcategoryDescription] = useState(null); // Keep if used, otherwise remove
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState(null);
  
  // Loading states
  const [isLoading, setIsLoading] = useState(true); // Default true to show spinner on load
  const [isDeleteConfirmModalVisible, setIsDeleteConfirmModalVisible] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  const [previousImageUrl, setPreviousImageUrl] = useState("");
  const [thumbnailFileList, setThumbnailFileList] = useState([]);

  const handleThumbnailFileChange = ({ fileList }) => {
    setThumbnailFileList(fileList);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/category/all-unhide`);
      // Ensure we always set an array
      setCategories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      message.error("Failed to load categories");
    } finally {
      setIsLoading(false);
    }
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const showUpdateModal = (
    categoryId,
    subCategoryId,
    subCategoryName,
    subCategoryThumbnail,
    displayOrder
  ) => {
    setSelectedCategoryId(categoryId);
    setSelectedSubcategoryId(subCategoryId);
    setUpdateSubcategoryName(subCategoryName);
    setPreviousImageUrl(subCategoryThumbnail);
    
    form.setFieldsValue({
      updateSubcategoryName: subCategoryName,
      updatedDisplayOrder: displayOrder,
    });

    setThumbnailFileList(
      subCategoryThumbnail
        ? [
            {
              uid: "-1",
              name: "Previous Image",
              status: "done",
              url: subCategoryThumbnail,
            },
          ]
        : []
    );
    setIsUpdateModalVisible(true);
  };

  const handleCancelUpdate = () => {
    setIsUpdateModalVisible(false);
  };

  const handleNewSubcategory = (categoryId) => {
    form.resetFields();
    setThumbnailFileList([]);
    setIsModalVisible(true);
    setSelectedCategoryId(categoryId);
  };

  const showDeleteConfirmModal = (product) => {
    setSelectedSubcategoryId(product);
    setIsDeleteConfirmModalVisible(true);
  };

  const hideDeleteConfirmModal = () => {
    setSelectedSubcategoryId(null);
    setIsDeleteConfirmModalVisible(false);
  };

  const handleDeleteSubcategory = async () => {
    try {
      setIsDeleting(true);
      await axios.delete(
        `${BASE_URL}/api/subcategories/${selectedSubcategoryId}`
      );

      message.success("Subcategory deleted successfully");
      setIsDeleteConfirmModalVisible(false);
      fetchData();
    } catch (error) {
      console.error("Error deleting subcategory:", error);
      message.error("Failed to delete subcategory");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateSubcategory = async () => {
    try {
      // Validate form first
      await form.validateFields();
      
      if (thumbnailFileList.length === 0) {
          message.error("Please upload an image");
          return;
      }

      setIsCreating(true);

      const formData = new FormData();
      formData.append("name", newSubcategoryName);
      // formData.append("description", newSubcategoryDescription); // Uncomment if backend uses this
      formData.append("displayOrder", displayOrder);
      formData.append("categoryId", selectedCategoryId);
      
      if(thumbnailFileList[0]?.originFileObj) {
        formData.append("subcategoryImage", thumbnailFileList[0].originFileObj);
      }

      await axios.post(
        `${BASE_URL}/api/subcategories`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      message.success("Subcategory created successfully");
      setIsModalVisible(false);
      setThumbnailFileList([]);
      fetchData();
    } catch (error) {
      console.error("Error creating subcategory:", error);
      message.error("Failed to create subcategory");
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateSubcategory = async () => {
    try {
      setIsUpdating(true);

      await axios.put(
        `${BASE_URL}/api/subcategories/${selectedSubcategoryId}`,
        {
          name: updateSubcategoryName,
          displayOrder: updatedDisplayOrder,
        }
      );

      const isNewImageUploaded =
        thumbnailFileList.length > 0 &&
        thumbnailFileList.some((file) => file.originFileObj);

      const imageToSend = isNewImageUploaded
        ? thumbnailFileList.find((file) => file.originFileObj).originFileObj
        : previousImageUrl;

      if (imageToSend && isNewImageUploaded) {
        const formData = new FormData();
        formData.append("id", selectedSubcategoryId);
        formData.append("image", imageToSend);

        await axios.put(
          `${BASE_URL}/api/subcategories/update-image`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        message.success("Subcategory and image updated successfully");
      } else {
        message.success("Subcategory updated successfully");
      }

      setIsUpdateModalVisible(false);
      if (isNewImageUploaded) setThumbnailFileList([]);
      
      fetchData();
    } catch (error) {
      console.error("Error updating subcategory:", error);
      message.error("Failed to update subcategory.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (source.droppableId !== destination.droppableId) return;

    const sourceCategoryId = parseInt(source.droppableId, 10);
    if (isNaN(sourceCategoryId)) return;

    const categoryIndex = categories.findIndex(
      (category) => category.categoryId === sourceCategoryId
    );

    if (categoryIndex === -1) return;

    const updatedCategories = [...categories];
    // Safety check: ensure subcategoryList exists
    const categorySubcategories = [
      ...(updatedCategories[categoryIndex].subcategoryList || []),
    ];

    const [movedSubcategory] = categorySubcategories.splice(source.index, 1);
    categorySubcategories.splice(destination.index, 0, movedSubcategory);

    // Optimistic UI Update
    updatedCategories[categoryIndex].subcategoryList = categorySubcategories;
    setCategories(updatedCategories);

    try {
      const updatePromises = categorySubcategories.map((subcategory, index) => {
        return axios.put(
          `${BASE_URL}/api/subcategories/update-display-order/${
            subcategory.subCategoryId
          }?displayOrder=${index + 1}`
        );
      });

      await Promise.all(updatePromises);
      message.success("Subcategories reordered successfully!");
    } catch (error) {
      console.error("Error updating display orders:", error);
      message.error("Failed to save new order.");
      fetchData(); // Revert on error
    }
  };

  const columns = [
    {
      title: "Category Name",
      dataIndex: "categoryName",
      key: "categoryName",
      width: "15%",
    },
    {
      title: "Subcategories",
      dataIndex: "subcategoryList",
      key: "subcategoryList",
      width: "70%",
      render: (subcategoryList, record) => {
        // --- SAFEGUARD: Handle undefined/null list ---
        const safeSubList = subcategoryList || []; 
        
        return (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable
            droppableId={record.categoryId.toString()}
            key={record.categoryId}
          >
            {(provided) => (
              <ul
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{
                  padding: 0,
                  listStyle: "none",
                  margin: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                {/* Use the safe list here */}
                {safeSubList.map((subcategory, index) => (
                  <Draggable
                    key={subcategory.subCategoryId}
                    draggableId={String(subcategory.subCategoryId)}
                    index={index}
                  >
                    {(provided) => (
                      <li
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                          background: "#f9f9f9",
                          padding: "10px",
                          border: "1px solid #d9d9d9",
                          borderRadius: "5px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>{subcategory.subCategoryName}</span>
                        <div>
                          <Button
                            icon={<EditOutlined />}
                            onClick={() => {
                              showUpdateModal(
                                record.categoryId,
                                subcategory.subCategoryId,
                                subcategory.subCategoryName,
                                subcategory.subCategoryImage,
                                subcategory.displayOrder
                              );
                            }}
                            style={{ marginRight: "8px" }}
                          />
                          <Button
                            icon={<DeleteOutlined />}
                            danger
                            onClick={() =>
                              showDeleteConfirmModal(subcategory.subCategoryId)
                            }
                          />
                        </div>
                      </li>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      )},
    },
    {
      title: "New Subcategory",
      dataIndex: "categoryName",
      key: "newSubcategory",
      width: "20%",
      render: (categoryName, record) => (
        <button
          className="w-[30px] h-[30px]"
          onClick={() => handleNewSubcategory(record.categoryId)}
        >
          {/* Replaced base64 with a simple icon for cleanliness, change back if needed */}
          <span style={{fontSize: '24px'}}>➕</span> 
        </button>
      ),
    },
  ];

  return userId ? (
    <div style={{ padding: 20 }}>
      <h1 className="my-3 md:text-3xl">Categories and Subcategories</h1>

      {isLoading ? (
        <Spin size="large" />
      ) : (
        <Table
          dataSource={categories}
          columns={columns}
          rowKey="categoryId" // Changed from _id to categoryId based on your data structure
          pagination={false}
          bordered
          size="middle"
          scroll={{ x: true }}
        />
      )}
      <Modal
        title="Create New Subcategory"
        visible={isModalVisible}
        onCancel={handleCancel}
        onOk={handleCreateSubcategory}
        okType="default"
        confirmLoading={isCreating}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Subcategory Name"
            name="newSubcategoryName"
            rules={[
              { required: true, message: "Please enter the subcategory name" },
            ]}
          >
            <Input onChange={(e) => setNewSubcategoryName(e.target.value)} />
          </Form.Item>
          <Form.Item
            label="Display Order"
            name="displayOrder"
            rules={[
              { required: true, message: "Please enter the display Order" },
            ]}
          >
            <Input onChange={(e) => setDisplayOrder(e.target.value)} />
          </Form.Item>
          <Form.Item
            label="SubCategory Thumbnail Image"
            name="subCategoryThumbnail"
            rules={[
              { required: true, message: "Please enter the subCategory name" },
            ]}
          >
            <Upload
              onChange={handleThumbnailFileChange}
              showUploadList={true}
              beforeUpload={() => false}
              fileList={thumbnailFileList}
            >
              <Button icon={<UploadOutlined />}>Upload Image</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Update Subcategory"
        visible={isUpdateModalVisible}
        onCancel={handleCancelUpdate}
        onOk={handleUpdateSubcategory}
        okType="default"
        confirmLoading={isUpdating}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Subcategory Name"
            name="updateSubcategoryName"
            rules={[
              { required: true, message: "Please enter the subcategory name" },
            ]}
          >
            <Input
              value={updateSubcategoryName}
              onChange={(e) => setUpdateSubcategoryName(e.target.value)}
            />
          </Form.Item>
          <Form.Item
            label="Display Order"
            name="updatedDisplayOrder"
            rules={[
              { required: true, message: "Please enter the Display Order" },
            ]}
          >
            <Input
              value={updatedDisplayOrder}
              onChange={(e) => setUpdatedDisplayOrder(e.target.value)}
            />
          </Form.Item>

          <Form.Item
            label="SubCategory Thumbnail Image"
            name="subCategoryThumbnail"
          >
            <Upload
              onChange={handleThumbnailFileChange}
              showUploadList={true}
              beforeUpload={() => false}
              fileList={thumbnailFileList}
              listType="picture"
            >
              <Button icon={<UploadOutlined />}>Upload Image</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Deletion"
        visible={isDeleteConfirmModalVisible}
        onOk={handleDeleteSubcategory}
        onCancel={hideDeleteConfirmModal}
        cancelText="No"
        okType="danger"
        confirmLoading={isDeleting}
      >
        <p>Are you sure you want to delete this Subcategory?</p>
      </Modal>
    </div>
  ) : (
    <span
      style={{
        fontSize: "20px",
        background: "white",
        padding: "20px",
        borderRadius: "10px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
      }}
    >
      Sign in to see Subcategories
    </span>
  );
};

export default Subcategories;